/**
 * Heute Fälligkeiten mock data + derivation helpers — Story R4.
 *
 * Wire-swap path: replace MOCK_HEUTE_RENEWALS with
 *   const { data } = useQuery(queryKeys.renewalsHeute(), () => apiClient.get('/renewals/heute'))
 * No reshape — MOCK_HEUTE_RENEWALS is already a RenewalHeuteDto.
 *
 * [Source: docs/concept/2026-06-26_post-stefan-roadmap-and-module-specs.md §39/§45 (R4)]
 */

import type { RenewalHeuteDto, RenewalBoardItemDto } from '@brokly/shared';
import { RENEWAL_BUCKETS, BUCKET_BOUNDARY_DAYS } from '@brokly/shared';

// ── Derivation helper types ───────────────────────────────────────────────────

export interface HeuteRenewalsSummary {
  /** overdue + le_14d + le_30d */
  dueWithin30: number;
  /** = dto.redCount */
  redCount: number;
  /** redList[0]?.days_left ?? null — redList is pre-sorted ASC (most urgent first) */
  mostUrgentDaysLeft: number | null;
  hasRed: boolean;
  hasAny: boolean;
}

/** Pure derivation — no React, no DOM. Unit-testable directly. */
export function deriveHeuteRenewalsSummary(dto: RenewalHeuteDto): HeuteRenewalsSummary {
  const dueWithin30 =
    dto.bucketCounts.overdue + dto.bucketCounts.le_14d + dto.bucketCounts.le_30d;
  const redCount = dto.redCount;
  const mostUrgentDaysLeft = dto.redList[0]?.days_left ?? null;
  return {
    dueWithin30,
    redCount,
    mostUrgentDaysLeft,
    hasRed: redCount > 0,
    hasAny: dueWithin30 > 0 || redCount > 0,
  };
}

// ── Sentence formatter (pure, exported for unit tests) ────────────────────────

/**
 * Returns the German sentence text WITHOUT colour markup —
 * used only for snapshot/unit assertions.
 * The JSX component splits the sentence to wrap the "rot"-fragment in a span.
 *
 * Example: "10 Policen werden in 30 Tagen fällig, 3 davon rot (in 5 Tagen)"
 */
export function formatHeuteSentence(summary: HeuteRenewalsSummary): string {
  const { dueWithin30, redCount, mostUrgentDaysLeft, hasRed } = summary;
  const base = `${dueWithin30} Policen werden in ${BUCKET_BOUNDARY_DAYS.le_30d} Tagen fällig`;
  if (!hasRed) return base;

  let redTail = '';
  if (mostUrgentDaysLeft === null) {
    redTail = '';
  } else if (mostUrgentDaysLeft < 0) {
    redTail = ' (überfällig)';
  } else if (mostUrgentDaysLeft === 0) {
    redTail = ' (heute fällig)';
  } else if (mostUrgentDaysLeft === 1) {
    redTail = ' (in 1 Tag)';
  } else {
    redTail = ` (in ${mostUrgentDaysLeft} Tagen)`;
  }

  return `${base}, ${redCount} davon rot${redTail}`;
}

// ── Mock red list items ───────────────────────────────────────────────────────
// redList is pre-sorted days_left ASC (most urgent first), nulls last.
// Status 'mahnen' on every item (redList = status='mahnen' contract).
// Company names / POL numbers match the R3 board mock for visual continuity.

const RED_ITEMS: RenewalBoardItemDto[] = [
  // Most urgent: overdue
  {
    policy_id: 'p-r1',
    policy_number: 'POL-339-2266',
    kunde: 'Basler Chemie AG',
    versicherer: 'CSS',
    sparte: 'Betriebshaftpflicht',
    praemie: '48200.00',
    currency: 'CHF',
    renewal_date: '2026-06-20',
    act_by_date: '2026-06-22',
    days_left: -2,
    bucket: 'overdue',
    status: 'mahnen',
  },
  // Second: 5 days left
  {
    policy_id: 'p-r2',
    policy_number: 'POL-118-7740',
    kunde: 'Genève Maritime SA',
    versicherer: 'Swiss Life',
    sparte: 'Transportversicherung',
    praemie: '73900.00',
    currency: 'CHF',
    renewal_date: '2026-07-10',
    act_by_date: '2026-07-01',
    days_left: 5,
    bucket: 'le_14d',
    status: 'mahnen',
  },
  // Third: 12 days left
  {
    policy_id: 'p-r3',
    policy_number: 'POL-551-9023',
    kunde: 'Alpina Pharma AG',
    versicherer: 'Helvetia',
    sparte: 'Sachversicherung',
    praemie: '31500.00',
    currency: 'CHF',
    renewal_date: '2026-07-12',
    act_by_date: '2026-07-08',
    days_left: 12,
    bucket: 'le_14d',
    status: 'mahnen',
  },
];

// ── Mock RenewalHeuteDto ──────────────────────────────────────────────────────
// bucketCounts:
//   overdue=2, le_14d=4, le_30d=4  → dueWithin30 = 10  (Stefan's literal example)
//   le_3mo=6, le_6mo=5, gt_6mo=9, unknown=1
// redCount = 3 = RED_ITEMS.length
// redList = RED_ITEMS (sorted ASC, most urgent first: -2, 5, 12)

export const MOCK_HEUTE_RENEWALS: RenewalHeuteDto = {
  bucketCounts: {
    overdue: 2,
    le_14d: 4,
    le_30d: 4,
    le_3mo: 6,
    le_6mo: 5,
    gt_6mo: 9,
    unknown: 1,
  },
  redCount: RED_ITEMS.length,
  redList: RED_ITEMS,
};

// ── Integrity guard (compile-time) ────────────────────────────────────────────

// Ensure RENEWAL_BUCKETS is referenced so tree-shakers don't drop it.
void RENEWAL_BUCKETS;
